import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  Calendar,
  CheckCircle2,
  Clock,
  FileText,
  Heart,
  Leaf,
  Package,
  ShoppingBag,
  ShieldCheck,
  Truck,
  XCircle,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import DecorLeaf from '../components/DecorLeaf';
import LoadingState from '../components/LoadingState';
import ErrorState from '../components/ErrorState';
import usePageMetadata from '../hooks/usePageMetadata';
import * as ordersApi from '../api/endpoints/orders';
import { getCurrentSessionId } from '../api/client';
import { isCommittedSale, saleStatusOf, splitGroupOutcome } from '../utils/splitCartReconciliation';
import { btnLg, btnPrimary, focusRing, homeShell } from '../styles/ui';

// Sale.paymentStatus / Sale.status enums (see backend Sale model) collapsed
// into the four outcomes this page actually needs to distinguish. A
// cancelled order only counts as "refunded" once money that was actually
// taken has actually come back — a cancelled COD order (never charged) or
// one still mid-refund (refund_pending) must not tell the customer they
// were already refunded.
const getOutcome = (order) => {
  if (order.paymentStatus === 'refunded') return 'refunded';
  if (order.status === 'cancelled' || order.paymentStatus === 'refund_pending') return 'cancelled';
  if (order.paymentStatus === 'processing' || order.paymentStatus === 'partial') return 'pending';
  return 'placed';
};

const OUTCOME_META = {
  placed: { Icon: CheckCircle2, title: 'Order Placed!', ring: 'bg-emerald-600', halo: 'bg-emerald-100' },
  pending: { Icon: Clock, title: 'Payment Processing', ring: 'bg-amber-500', halo: 'bg-amber-100' },
  refunded: { Icon: XCircle, title: 'Order Refunded', ring: 'bg-red-600', halo: 'bg-red-100' },
  cancelled: { Icon: XCircle, title: 'Order Cancelled', ring: 'bg-red-600', halo: 'bg-red-100' },
};

const groupSaleLabel = (sale) => {
  if (sale.paymentStatus === 'refunded') return 'Refunded';
  if (sale.paymentStatus === 'refund_failed') return 'Refund failed';
  if (sale.paymentStatus === 'refund_pending') return 'Refund in progress';
  if (isCommittedSale(sale)) return 'Placed';
  if (['cancelled', 'refunded', 'rejected'].includes(saleStatusOf(sale))) return 'Not placed';
  return 'Processing';
};

// Claims already approved elsewhere on the site (BenefitsStrip). The
// reference's "Handpicked from trusted farms" is a sourcing claim this app
// cannot support, so the approved wording is used instead.
const REASSURANCE = [
  { Icon: Leaf, title: 'Premium Quality', subtitle: 'Sourced from trusted partners' },
  { Icon: ShieldCheck, title: 'Hygienically Packed', subtitle: "For your family's safety" },
  { Icon: Truck, title: 'Same-Day Delivery', subtitle: 'Freshness at your doorstep' },
  { Icon: Heart, title: 'Thank You', subtitle: 'For choosing Fresh & Homely' },
];

const formatDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return {
    date: date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }),
    time: date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }),
  };
};

const InfoTile = ({ icon: Icon, label, value, hint }) => (
  <div className="flex items-center gap-3.5 px-5 py-4 min-w-0">
    <span className="w-11 h-11 rounded-2xl bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0">
      <Icon size={20} aria-hidden="true" />
    </span>
    <span className="min-w-0">
      <span className="block text-xs text-ink-secondary">{label}</span>
      <span className="block text-[15px] font-bold text-emerald-950 break-words">{value}</span>
      {hint && <span className="block text-xs text-ink-secondary mt-0.5">{hint}</span>}
    </span>
  </div>
);

const OrderConfirmationPage = () => {
  const { saleId } = useParams();
  const [searchParams] = useSearchParams();
  const groupId = searchParams.get('group');
  const [state, setState] = useState({ status: 'loading', data: null, error: null });
  const [groupSales, setGroupSales] = useState(null);
  const requestIdRef = useRef(0);
  const invalidateRequests = useCallback(() => {
    ++requestIdRef.current;
  }, []);

  usePageMetadata({
    title: 'Order Confirmed | Fresh & Homely',
    description: 'Your Fresh & Homely order has been placed.',
    canonicalPath: `/order-confirmation/${saleId}`,
  });

  const load = useCallback(() => {
    const requestId = ++requestIdRef.current;
    const session = getCurrentSessionId();
    setState({ status: 'loading', data: null, error: null });
    ordersApi
      .getOrder(saleId, { session })
      .then((response) => {
        if (requestId === requestIdRef.current && session === getCurrentSessionId()) {
          setState({ status: 'success', data: response.data, error: null });
        }
      })
      .catch((err) => {
        if (requestId === requestIdRef.current && session === getCurrentSessionId()) {
          setState({ status: 'error', data: null, error: err.message });
        }
      });
  }, [saleId]);

  useEffect(() => {
    load();
    return invalidateRequests;
  }, [load, invalidateRequests]);

  useEffect(() => {
    setGroupSales(null);
    if (!groupId) return;
    const session = getCurrentSessionId();
    let cancelled = false;
    let timeoutId;
    const current = () => !cancelled && session === getCurrentSessionId();
    const poll = async () => {
      let retry = true;
      try {
        const { data } = await ordersApi.getCheckoutGroup(groupId, { session });
        if (!current()) return;
        const sales = data?.sales || [];
        setGroupSales(sales);
        retry = splitGroupOutcome(data) !== 'settled' || sales.some(sale => sale.paymentStatus === 'refund_pending');
      } catch {
        // Keep any known outcomes while retrying a transient failure.
      }
      if (current() && retry) timeoutId = setTimeout(poll, 5000);
    };
    poll();
    return () => { cancelled = true; clearTimeout(timeoutId); };
  }, [groupId, saleId]);

  // Processing payments and refunds both need silent polling. Refunded and
  // other terminal states stop polling, while refund_pending keeps the
  // cancellation/refund-processing copy visible between requests.
  useEffect(() => {
    const paymentStatus = state.status === 'success' ? state.data.paymentStatus : null;
    const shouldPoll = state.status === 'success' && (
      getOutcome(state.data) === 'pending' || paymentStatus === 'refund_pending'
    );
    if (!shouldPoll) return undefined;

    let cancelled = false;
    let timeoutId;
    const session = getCurrentSessionId();
    const poll = async () => {
      const requestId = ++requestIdRef.current;
      try {
        const response = await ordersApi.getOrder(saleId, { session });
        if (!cancelled && requestId === requestIdRef.current && session === getCurrentSessionId()) {
          setState({ status: 'success', data: response.data, error: null });
        }
      } catch {
        // Transient poll failure — keep the current pending view and retry.
      } finally {
        if (!cancelled && session === getCurrentSessionId()) timeoutId = setTimeout(poll, 5000);
      }
    };

    timeoutId = setTimeout(poll, 5000);
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      invalidateRequests();
    };
  }, [saleId, state.status, state.data, invalidateRequests]);

  const order = state.status === 'success' ? state.data : null;
  const outcome = order ? getOutcome(order) : null;
  const meta = outcome ? OUTCOME_META[outcome] : null;
  const invoiceSuffix = order?.invoiceNumber ? ` — invoice ${order.invoiceNumber}` : '';
  const placedAt = formatDate(order?.createdAt);
  // A real ETA when the backend has set one; otherwise the slot the customer
  // actually chose, labelled as the slot it is rather than as an estimate.
  const estimatedAt = formatDate(order?.fulfillment?.estimatedDeliveryAt);
  const slotLabel =
    order?.deliverySlot?.label ||
    [order?.deliverySlot?.startTime, order?.deliverySlot?.endTime].filter(Boolean).join(' – ') ||
    null;
  const celebratory = outcome === 'placed' || outcome === 'pending';

  return (
    <>
      <Navbar />

      <main>
        <section className="relative overflow-hidden pt-24 sm:pt-28 pb-12 sm:pb-16 bg-gradient-to-b from-emerald-50/70 via-white to-emerald-50/40">
          {/* Decor only: owned inline leaves. */}
          <div className="pointer-events-none select-none absolute inset-0 overflow-hidden" aria-hidden="true">
            <DecorLeaf className="hidden sm:block absolute left-[8%] top-28 w-14 text-emerald-500/40 -rotate-[24deg]" />
            <DecorLeaf className="hidden lg:block absolute left-[14%] bottom-28 w-16 text-emerald-600/25 rotate-[8deg]" />
            <DecorLeaf className="hidden lg:block absolute right-[10%] top-32 w-16 text-emerald-500/35 rotate-[22deg]" />
            <DecorLeaf className="hidden lg:block absolute right-[22%] bottom-24 w-10 text-emerald-600/30 rotate-[40deg]" />
            <DecorLeaf className="hidden xl:block absolute left-[26%] bottom-16 w-9 text-emerald-600/25 -rotate-[14deg]" />
          </div>

          <div className={`${homeShell} relative`}>
            {state.status === 'loading' && <LoadingState label="Loading order..." />}
            {state.status === 'error' && <ErrorState message={state.error} onRetry={load} />}

            {order && meta && (
              <div className="max-w-3xl mx-auto text-center">
                {/* Status badge with a soft halo — CSS, not an image asset. */}
                <span className="relative inline-flex items-center justify-center">
                  <span className={`absolute inset-0 -m-4 rounded-full ${meta.halo} opacity-70 blur-[2px]`} aria-hidden="true" />
                  <span className={`relative w-[72px] h-[72px] sm:w-20 sm:h-20 rounded-full ${meta.ring} text-white flex items-center justify-center shadow-glass`}>
                    <meta.Icon size={38} aria-hidden="true" />
                  </span>
                </span>

                <h1 className="serif text-4xl sm:text-5xl xl:text-[3.5rem] text-emerald-950 leading-[1.05] mt-7">
                  {meta.title}
                </h1>

                <p className="text-base xl:text-lg text-ink-secondary leading-relaxed mt-4 max-w-xl mx-auto">
                  {outcome === 'placed' &&
                    `Thank you for your order${invoiceSuffix}. We'll get it ready for you soon.`}
                  {outcome === 'pending' &&
                    `We're still confirming your payment${invoiceSuffix}. This page will update once it clears — check your order history for the latest status.`}
                  {outcome === 'refunded' &&
                    `Your payment was refunded${invoiceSuffix}. Please check your order history for details.`}
                  {outcome === 'cancelled' &&
                    (order.paymentStatus === 'refund_pending'
                      ? `This order was cancelled${invoiceSuffix}. Your refund is being processed and will reflect soon.`
                      : `This order was cancelled${invoiceSuffix}.`)}
                </p>

                {/* Every tile is a field the order actually carries. */}
                {(placedAt || order.invoiceNumber || estimatedAt || slotLabel) && (
                  <div className="mt-9 inline-flex flex-col sm:flex-row flex-wrap items-stretch justify-center bg-white rounded-[1.5rem] border border-emerald-100 shadow-soft divide-y sm:divide-y-0 sm:divide-x divide-emerald-50 text-left max-w-full">
                    {placedAt && (
                      <InfoTile
                        icon={Calendar}
                        label="Order Date"
                        value={placedAt.date}
                        hint={placedAt.time}
                      />
                    )}
                    {order.invoiceNumber && (
                      <InfoTile icon={FileText} label="Order Number" value={order.invoiceNumber} />
                    )}
                    {estimatedAt ? (
                      <InfoTile
                        icon={Truck}
                        label="Estimated Delivery"
                        value={estimatedAt.date}
                        hint={estimatedAt.time}
                      />
                    ) : (
                      slotLabel && <InfoTile icon={Truck} label="Delivery Slot" value={slotLabel} />
                    )}
                  </div>
                )}

                {groupSales && groupSales.length > 0 && (
                  <div className="mt-9 max-w-md mx-auto">
                    <p className="text-sm font-semibold text-ink-secondary mb-3">
                      {groupSales.filter(isCommittedSale).length} of {groupSales.length} orders placed
                    </p>
                    <div className="space-y-2">
                      {groupSales.map((sibling) => (
                        <Link
                          key={sibling.saleId}
                          to={`/orders/${sibling.saleId}`}
                          className="flex items-center justify-between px-4 py-3 rounded-xl bg-white border border-emerald-100 hover:bg-emerald-50 transition-colors text-sm"
                        >
                          <span className="font-medium text-emerald-900">
                            {sibling.invoiceNumber || `Order ${(sibling.index || 0) + 1}`}
                          </span>
                          <span className="text-ink-secondary">{groupSaleLabel(sibling)} →</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-9">
                  <Link to={`/orders/${saleId}`} className={`${btnPrimary} ${btnLg} gap-2 w-full sm:w-auto`}>
                    <Package size={19} aria-hidden="true" />
                    View Order
                  </Link>
                  <Link
                    to="/categories"
                    className={`w-full sm:w-auto inline-flex items-center justify-center gap-2 h-12 px-8 rounded-full bg-white border border-emerald-200 text-emerald-800 text-base font-semibold hover:bg-emerald-50 transition-colors ${focusRing}`}
                  >
                    <ShoppingBag size={19} aria-hidden="true" />
                    Continue Shopping
                  </Link>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Reassurance reads as celebration, so it is suppressed for a
            cancelled or refunded order. */}
        {celebratory && (
          <section className="pb-12 sm:pb-16 bg-white">
            <div className={homeShell}>
              <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4 rounded-[1.5rem] bg-emerald-50/60 border border-emerald-100 p-5 sm:p-6 lg:divide-x lg:divide-emerald-100">
                {REASSURANCE.map(({ Icon, title, subtitle }) => (
                  <li key={title} className="flex items-center gap-4 px-2 lg:px-5">
                    <span className="w-12 h-12 rounded-full bg-white shadow-soft text-emerald-700 flex items-center justify-center shrink-0">
                      <Icon size={21} aria-hidden="true" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-[15px] font-bold text-emerald-950">{title}</span>
                      <span className="block text-sm text-ink-secondary mt-0.5">{subtitle}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
};

export default OrderConfirmationPage;
